import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { ClerkProvider } from '@clerk/clerk-react';
import { clerkLocalizations } from './i18n/clerk-localization';
import { BrowserRouter } from 'react-router-dom';
import App from './app/App';
import { LanguageProvider, useI18n, detectLanguage, applyDocumentLanguage } from './i18n';
import './styles/index.css';

// Extend window interface for env
declare global {
  interface Window {
    __ENV__?: {
      CLERK_PUBLISHABLE_KEY?: string;
      API_URL?: string;
      STRIPE_PRICE_MONTHLY?: string;
      STRIPE_PRICE_ANNUAL?: string;
    };
  }
}

// Get Clerk publishable key from environment or window
const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || window.__ENV__?.CLERK_PUBLISHABLE_KEY;

if (!clerkPubKey) {
  console.warn('Missing Clerk publishable key - authentication will not work');
}

function LocalizedClerkProvider({ children }: { children: ReactNode }) {
  const { lang } = useI18n();
  return (
    <ClerkProvider publishableKey={clerkPubKey || ''} localization={clerkLocalizations[lang]}>
      {children}
    </ClerkProvider>
  );
}

// Apply direction before the first rendered loading/auth screen.
applyDocumentLanguage(detectLanguage());

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <LanguageProvider>
      <LocalizedClerkProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </LocalizedClerkProvider>
    </LanguageProvider>
  </StrictMode>
);
